import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { TimePreferenceGrid } from "@/components/TimePreferenceGrid";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft, Plus, Trash2, Users, Trophy, CalendarDays, Settings, Loader2,
  ExternalLink, RefreshCw, MapPin, Edit, Edit3, Undo2, Redo2, FileDown, CheckCircle, Clock, UserPlus, GripVertical,
  Upload, Download
} from "lucide-react";
import { CourtAvailability } from "@/components/CourtAvailability";
import { Checkbox } from "@/components/ui/checkbox";
import { BracketPanel } from "@/components/BracketPanel";
import { SchedulePanel } from "@/components/SchedulePanel";

const DAYS = [
  { key: "monday", label: "Lunedi" }, { key: "tuesday", label: "Martedi" },
  { key: "wednesday", label: "Mercoledi" }, { key: "thursday", label: "Giovedi" },
  { key: "friday", label: "Venerdi" }, { key: "saturday", label: "Sabato" },
  { key: "sunday", label: "Domenica" },
];
const WEEKDAY_KEYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

/** Convert legacy weekday-based courts_config to date-based when dates are known. */
function expandCourtsToDateBased(courtsConfig, startDate, endDate) {
  if (!startDate || !endDate || !courtsConfig) return {};
  const keys = Object.keys(courtsConfig);
  if (keys.length === 0) return {};
  // If already date-based (any key matches YYYY-MM-DD) return as-is
  if (keys.some(k => /^\d{4}-\d{2}-\d{2}$/.test(k))) return courtsConfig;
  // Legacy weekday-based → expand to each date in range
  const result = {};
  const current = new Date(startDate + "T12:00:00");
  const end = new Date(endDate + "T12:00:00");
  while (current <= end) {
    const dateStr = current.toISOString().split("T")[0];
    const dayKey = WEEKDAY_KEYS[current.getDay()];
    result[dateStr] = courtsConfig[dayKey] ?? 0;
    current.setDate(current.getDate() + 1);
  }
  return result;
}
const ROUND_LABELS = {
  R256: "Turno Prel.", R128: "128-esimi", R64: "64-esimi", R32: "32-esimi",
  R16: "16-esimi", QF: "Quarti", SF: "Semifinale", F: "Finale",
};
const RANK_VAL = {"1.1":100,"1.2":99,"1.3":98,"1.4":97,"1.5":96,"1.6":95,"2.1":90,"2.2":89,"2.3":88,"2.4":87,"2.5":86,"2.6":85,"2.7":84,"2.8":83,"3.1":80,"3.2":79,"3.3":78,"3.4":77,"3.5":76,"3.6":75,"4.1":70,"4.2":69,"4.3":68,"4.4":67,"4.5":66,"4.6":65,"4.NC":50};

// ==================== PLAYER IMPORT CARD ====================
const TournamentSettingsCard = ({ maxMatchesPerDay, onMaxMatchesChange, defaultRestTime, onRestTimeChange }) => (
  <Card className="bg-white">
    <CardContent className="space-y-4 pt-6">
      <div>
        <Label className="text-xs font-bold uppercase tracking-wide">Max Incontri per Giocatore al Giorno</Label>
        <Input data-testid="edit-max-matches-per-day" type="number" min="1" max="10" value={maxMatchesPerDay}
          onChange={(e) => onMaxMatchesChange(parseInt(e.target.value) || 3)} className="mt-1 rounded-sm w-24" />
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wide">Tempo di Riposo Predefinito (minuti)</Label>
        <p className="text-[10px] text-slate-400 mt-0.5 mb-1">Tempo minimo tra due partite dello stesso giocatore. Viene applicato a tutti i giocatori al salvataggio, ma resta modificabile individualmente.</p>
        <Input data-testid="edit-default-rest-time" type="number" min="0" max="480" value={defaultRestTime}
          onChange={(e) => onRestTimeChange(parseInt(e.target.value) || 0)} className="mt-1 rounded-sm w-24" />
      </div>
    </CardContent>
  </Card>
);

const PlayerImportCard = ({ tournament, onRefresh }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;
  const downloadTemplate = () => {
    // Most reliable cross-browser pattern: open the URL in a new tab.
    // The backend sets `Content-Disposition: attachment` so the browser will
    // download the file instead of rendering it. This avoids:
    //  - Chrome silently blocking blob downloads created from fetch responses
    //  - cross-origin `<a download>` having the `download` attribute ignored
    //  - CORS preflight headaches with credentials
    const url = `${API}/api/tournaments/${tournament.tournament_id}/players/template`;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      // Popup blocker active → fall back to same-tab navigation. The
      // attachment header still triggers a download in the active tab.
      window.location.href = url;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    setResult(null);
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    const url = `${API_BASE}/api/tournaments/${tournament.tournament_id}/players/upload`;
    const formData = new FormData();
    formData.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.withCredentials = true;
    xhr.onload = () => {
      setUploading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          setResult({ type: "success", message: data.message, detail: `Categorie create: ${data.created_categories} | Giocatori: ${data.created_players} | Righe saltate: ${data.skipped_rows}` });
          onRefresh?.();
        } else {
          setResult({ type: "error", message: data?.detail || "Errore durante il caricamento" });
        }
      } catch {
        setResult({ type: "error", message: "Risposta non valida dal server" });
      }
    };
    xhr.onerror = () => { setUploading(false); setResult({ type: "error", message: "Errore di rete durante il caricamento" }); };
    xhr.send(formData);
  };

  return (
    <Card className="bg-white">
      <CardContent className="space-y-4 pt-6">
        <div>
          <p className="font-heading text-lg font-bold uppercase">Importa Giocatori da File</p>
          <p className="text-xs text-slate-500 mt-1">Carica un file Excel per importare automaticamente giocatori e categorie.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            data-testid="download-template-btn"
            variant="outline"
            className="rounded-sm gap-2 border-green-700 text-green-700 hover:bg-green-50 flex-1"
            onClick={downloadTemplate}
          >
            <Download className="h-4 w-4" /> Scarica Template
          </Button>
          <Button
            data-testid="upload-players-btn"
            className="rounded-sm gap-2 bg-green-700 hover:bg-green-800 text-white flex-1"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Caricamento..." : "Carica File Excel"}
          </Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />
        </div>
        {result && (
          <div data-testid="import-result" className={`rounded-sm p-3 text-sm ${result.type === "success" ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}>
            <p className="font-semibold">{result.message}</p>
            {result.detail && <p className="text-xs mt-1 text-slate-600">{result.detail}</p>}
          </div>
        )}
        <div className="bg-slate-50 rounded-sm p-3 text-xs text-slate-500 space-y-1">
          <p className="font-semibold text-slate-600">Colonne del file Excel:</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li><span className="font-medium">Gara / Tornei</span> — nome categoria (es. "Singolare U14 Maschile")</li>
            <li>Cognome e Nome</li>
            <li>Sesso (M/F)</li>
            <li>Cat. Età (U10, U12 ... O80)</li>
            <li>Ranking (4.NC, 4.1 ... 1.1)</li>
            <li>Circolo / Associazione</li>
            <li>Email</li>
            <li>Telefono</li>
            <li>Preferenze Orario (9-13 / 13-17 / 17-21)</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== INFO PANEL ====================
const InfoPanel = ({ tournament, onUpdate }) => {
  const [name, setName] = useState(tournament.name);
  const [startDate, setStartDate] = useState(tournament.start_date);
  const [endDate, setEndDate] = useState(tournament.end_date);
  const [courts, setCourts] = useState(() =>
    expandCourtsToDateBased(tournament.courts_config || {}, tournament.start_date, tournament.end_date)
  );
  const [maxMatchesPerDay, setMaxMatchesPerDay] = useState(tournament.max_matches_per_day || 1);
  const [roundDayPrefs, setRoundDayPrefs] = useState(tournament.round_day_preferences || {});
  const [defaultRestTime, setDefaultRestTime] = useState(tournament.default_rest_time_minutes || 30);
  const [courtAvailability, setCourtAvailability] = useState(tournament.court_availability || {});
  const [defaultCourtAvailability, setDefaultCourtAvailability] = useState(tournament.default_court_availability || {});
  const [globalCourtAvailability, setGlobalCourtAvailability] = useState(tournament.global_court_availability || []);
  const [slotPriority, setSlotPriority] = useState(() => {
    const sp = tournament.slot_priority;
    if (Array.isArray(sp) && sp.length > 0) return sp.join(",");
    return "1,2,0"; // default: pomeriggio → sera → mattina
  });
  const [courtNames, setCourtNames] = useState(tournament.court_names || {});
  const [matchDirector, setMatchDirector] = useState(tournament.match_director || "");
  const [chiefUmpire, setChiefUmpire] = useState(tournament.chief_umpire || "");
  const [emailOnScheduleChange, setEmailOnScheduleChange] = useState(tournament.email_on_schedule_change ?? true);
  const [reminderIntervals, setReminderIntervals] = useState(tournament.reminder_intervals || ["48h", "24h", "2h"]);
  const [saving, setSaving] = useState(false);

  // When date range changes, sync courts: keep existing date values, add new dates with 0
  useEffect(() => {
    if (!startDate || !endDate) return;
    setCourts(prev => {
      const result = {};
      const current = new Date(startDate + "T12:00:00");
      const end = new Date(endDate + "T12:00:00");
      while (current <= end) {
        const dateStr = current.toISOString().split("T")[0];
        result[dateStr] = prev[dateStr] ?? 0;
        current.setDate(current.getDate() + 1);
      }
      return result;
    });
  }, [startDate, endDate]);

  // Sorted dates in the current tournament range
  const tournamentDateEntries = useMemo(() => {
    if (!startDate || !endDate) return [];
    const entries = [];
    const current = new Date(startDate + "T12:00:00");
    const end = new Date(endDate + "T12:00:00");
    while (current <= end) {
      const dateStr = current.toISOString().split("T")[0];
      const dayLabel = current.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" });
      entries.push({ dateStr, dayLabel });
      current.setDate(current.getDate() + 1);
    }
    return entries;
  }, [startDate, endDate]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tournaments/${tournament.tournament_id}`, { name, start_date: startDate, end_date: endDate, courts_config: courts, max_matches_per_day: maxMatchesPerDay, round_day_preferences: roundDayPrefs, default_rest_time_minutes: defaultRestTime, court_availability: courtAvailability, default_court_availability: defaultCourtAvailability, global_court_availability: globalCourtAvailability, slot_priority: slotPriority.split(",").map(s => parseInt(s, 10)).filter(n => !isNaN(n)), court_names: courtNames, match_director: matchDirector, chief_umpire: chiefUmpire, email_on_schedule_change: emailOnScheduleChange, reminder_intervals: reminderIntervals });
      toast.success("Torneo aggiornato con successo.");
      onUpdate();
    } catch (e) { toast.error("Errore"); } finally { setSaving(false); }
  };
  const ROUND_KEYS = ["QF", "SF", "F"];
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-6">
        <Card className="bg-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg font-bold uppercase">Dettagli</CardTitle>
          <Button data-testid="save-tournament-btn" onClick={save} disabled={saving} className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Salva
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">Nome Torneo</Label>
            <Input data-testid="edit-tournament-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Direttore di Gara</Label>
              <Input data-testid="edit-match-director" value={matchDirector} onChange={(e) => setMatchDirector(e.target.value)} placeholder="Nome e cognome" className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Giudice Arbitro</Label>
              <Input data-testid="edit-chief-umpire" value={chiefUmpire} onChange={(e) => setChiefUmpire(e.target.value)} placeholder="Nome e cognome" className="mt-1 rounded-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase">Data Inizio</Label>
              <Input data-testid="edit-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Data Fine</Label>
              <Input data-testid="edit-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 rounded-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">Giorno Preferito per Turno</Label>
            <p className="text-[10px] text-slate-400 mt-0.5 mb-2">Scegli in quale giorno preferisci programmare semifinali, finali, ecc.</p>
            {ROUND_KEYS.map((rk) => (
              <div key={rk} className="flex items-center gap-3 mb-2">
                <span className="text-sm w-28 text-slate-600 font-medium">{ROUND_LABELS[rk]}</span>
                <Select value={roundDayPrefs[rk] || "any"} onValueChange={(v) => {
                  const updated = { ...roundDayPrefs };
                  if (v === "any") { delete updated[rk]; } else { updated[rk] = v; }
                  setRoundDayPrefs(updated);
                }}>
                  <SelectTrigger data-testid={`round-day-${rk}`} className="w-40 rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Qualsiasi</SelectItem>
                    {DAYS.map(d => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">Ordine di Riempimento Fasce Orarie</Label>
            <p className="text-[10px] text-slate-400 mt-0.5 mb-2">Quali fasce orarie riempire per prime quando i giocatori non hanno preferenze.</p>
            <Select value={slotPriority} onValueChange={setSlotPriority}>
              <SelectTrigger data-testid="slot-priority-select" className="w-full rounded-sm h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0,1,2">Mattina → Pomeriggio → Sera (09-13 → 13-17 → 17-21)</SelectItem>
                <SelectItem value="1,2,0">Pomeriggio → Sera → Mattina (13-17 → 17-21 → 09-13)</SelectItem>
                <SelectItem value="2,0,1">Sera → Mattina → Pomeriggio (17-21 → 09-13 → 13-17)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      <TournamentSettingsCard
        maxMatchesPerDay={maxMatchesPerDay}
        onMaxMatchesChange={setMaxMatchesPerDay}
        defaultRestTime={defaultRestTime}
        onRestTimeChange={setDefaultRestTime}
      />
      <PlayerImportCard
        tournament={tournament}
        onRefresh={onUpdate}
      />
      <Card className="bg-white border-green-200">
          <CardHeader><CardTitle className="font-heading text-lg font-bold uppercase">Notifiche Email</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-start gap-3">
              <Checkbox
                id="email-schedule-change"
                data-testid="email-schedule-change"
                checked={emailOnScheduleChange}
                onCheckedChange={setEmailOnScheduleChange}
                className="mt-0.5"
              />
              <div>
                <label htmlFor="email-schedule-change" className="text-sm font-medium cursor-pointer">
                  Invia email su cambio programmazione
                </label>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Quando il calendario di un giocatore cambia, invia automaticamente una notifica via email.
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Promemoria prima del match</Label>
              <p className="text-[10px] text-slate-400 mt-0.5 mb-3">Attiva e personalizza gli intervalli di avviso (in ore).</p>
              <div className="space-y-2.5">
                {reminderIntervals.map((val, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Checkbox
                      id={`reminder-${idx}`}
                      data-testid={`reminder-check-${idx}`}
                      checked={true}
                      onCheckedChange={() => {
                        setReminderIntervals(prev => prev.filter((_, i) => i !== idx));
                      }}
                    />
                    <Input
                      data-testid={`reminder-hours-${idx}`}
                      type="number" min="1" max="168"
                      value={parseInt(val) || ""}
                      onChange={(e) => {
                        const h = parseInt(e.target.value) || 0;
                        setReminderIntervals(prev => prev.map((v, i) => i === idx ? `${h}h` : v));
                      }}
                      className="w-20 h-8 rounded-sm text-center"
                    />
                    <span className="text-sm text-slate-500">ore prima</span>
                    <button
                      data-testid={`reminder-remove-${idx}`}
                      onClick={() => setReminderIntervals(prev => prev.filter((_, i) => i !== idx))}
                      className="ml-auto text-slate-400 hover:text-red-500 transition-colors p-1"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <Button
                  data-testid="add-reminder-btn"
                  variant="outline" size="sm"
                  className="rounded-sm gap-1 text-xs mt-1"
                  onClick={() => setReminderIntervals(prev => [...prev, "12h"])}
                >
                  <Plus className="h-3 w-3" /> Aggiungi promemoria
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
    </div>
    <div className="space-y-6">
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-bold uppercase">Disponibilità Campi</CardTitle>
            <p className="text-xs text-slate-400 mt-0.5">Per ogni giorno del torneo imposta il numero di campi disponibili, il loro nome e le eventuali fasce di non disponibilità.</p>
          </CardHeader>
          <CardContent>
            <CourtAvailability
              courts={courts}
              onCourtsChange={setCourts}
              courtNames={courtNames}
              onCourtNamesChange={setCourtNames}
              availability={courtAvailability}
              onChange={setCourtAvailability}
              defaultAvailability={defaultCourtAvailability}
              onDefaultChange={setDefaultCourtAvailability}
              globalAvailability={globalCourtAvailability}
              onGlobalChange={setGlobalCourtAvailability}
              startDate={startDate}
              endDate={endDate}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== CATEGORIES PANEL ====================
const CategoriesPanel = ({ tournament, onRefresh }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // null = add, object = edit
  const [catName, setCatName] = useState("");
  const [duration, setDuration] = useState(60);
  const [maxPlayers, setMaxPlayers] = useState(256);

  const resetForm = () => { setCatName(""); setDuration(60); setMaxPlayers(256); setEditingCat(null); };

  const openAdd = () => { resetForm(); setShowDialog(true); };
  const openEdit = (cat) => {
    setEditingCat(cat);
    setCatName(cat.name);
    setDuration(cat.match_duration_minutes || 60);
    setMaxPlayers(cat.max_players || 256);
    setShowDialog(true);
  };

  const saveCategory = async () => {
    if (!catName.trim()) { toast.error("Inserisci il nome"); return; }
    const payload = { name: catName, match_duration_minutes: duration, max_players: maxPlayers };
    try {
      if (editingCat) {
        await api.put(`/tournaments/${tournament.tournament_id}/categories/${editingCat.category_id}`, payload);
        toast.success("Categoria aggiornata");
      } else {
        await api.post(`/tournaments/${tournament.tournament_id}/categories`, payload);
        toast.success("Categoria aggiunta");
      }
      onRefresh(); setShowDialog(false); resetForm();
    } catch (e) { toast.error(e.message?.includes("403") ? "Limite categorie raggiunto. Effettua l'upgrade." : "Errore"); }
  };

  const deleteCategory = async (cid) => {
    try { await api.del(`/tournaments/${tournament.tournament_id}/categories/${cid}`); toast.success("Eliminata"); onRefresh(); } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg font-bold uppercase">Categorie</CardTitle>
          <Button data-testid="add-category-btn" onClick={openAdd} className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm gap-1" size="sm"><Plus className="h-4 w-4" /> Aggiungi</Button>
        </CardHeader>
        <CardContent>
          {tournament.categories.length === 0 ? <p className="text-slate-500 text-center py-4">Nessuna categoria</p> : (
            <div className="space-y-1">{tournament.categories.map((cat) => (
              <div key={cat.category_id} data-testid={`category-row-${cat.category_id}`} className="flex items-center justify-between py-2 px-3 border border-slate-200 rounded-sm">
                <div><p className="font-medium text-sm">{cat.name}</p><p className="text-xs text-slate-400">{cat.match_duration_minutes} min &middot; max {cat.max_players} giocatori</p></div>
                <div className="flex items-center gap-1">
                  <Button data-testid={`edit-category-${cat.category_id}`} variant="ghost" size="sm" onClick={() => openEdit(cat)} className="text-slate-500 hover:text-green-700 h-7 w-7 p-0"><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteCategory(cat.category_id)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}</div>
          )}

          {/* Add / Edit Category Dialog */}
          <Dialog open={showDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowDialog(open); }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase">{editingCat ? "Modifica Categoria" : "Nuova Categoria"}</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div><Label className="text-xs font-bold uppercase">Nome</Label><Input data-testid="category-name-input" value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="es. Under 12 Maschile" className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs font-bold uppercase">Durata Partita (min)</Label><Input data-testid="match-duration-input" type="number" value={duration} onChange={(e) => setDuration(parseInt(e.target.value) || 60)} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs font-bold uppercase">Max Giocatori</Label><Input data-testid="max-players-input" type="number" value={maxPlayers} onChange={(e) => setMaxPlayers(parseInt(e.target.value) || 256)} className="mt-1 rounded-sm" /></div>
                <Button data-testid="submit-category-btn" onClick={saveCategory} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">{editingCat ? "Salva Modifiche" : "Aggiungi"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
      <PlayerImportCard tournament={tournament} onRefresh={onRefresh} />
    </div>
  );
};

// ==================== PLAYERS PANEL ====================
const PlayersPanel = ({ tournament, onRefresh }) => {
  const [selectedCat, setSelectedCat] = useState(tournament.categories[0]?.category_id || "");
  const [players, setPlayers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null); // null = add mode, object = edit mode
  const [playerName, setPlayerName] = useState("");
  const [playerEmail, setPlayerEmail] = useState("");
  const [playerPhone, setPlayerPhone] = useState("");
  const [preferences, setPreferences] = useState({});
  const [restTime, setRestTime] = useState(30);
  const [playWith, setPlayWith] = useState([]);
  const [playerRanking, setPlayerRanking] = useState("4.NC");
  const [isCustomRanking, setIsCustomRanking] = useState(false);
  const [playerClub, setPlayerClub] = useState("");
  const [playerGender, setPlayerGender] = useState("M");
  const [playerAgeCategory, setPlayerAgeCategory] = useState("");
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [sortField, setSortField] = useState(null); // "name" | "ranking"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedPlayers = useMemo(() => {
    if (!sortField) return players;
    const arr = [...players];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") {
        cmp = (a.name || "").localeCompare(b.name || "", "it");
      } else if (sortField === "ranking") {
        cmp = (RANK_VAL[a.ranking] || 0) - (RANK_VAL[b.ranking] || 0);
      }
      return sortDir === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [players, sortField, sortDir]);

  const fetchPlayers = useCallback(async () => {
    if (!selectedCat) return;
    setLoadingPlayers(true);
    try { const data = await api.get(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/players`); setPlayers(data); } catch (e) { toast.error("Errore"); } finally { setLoadingPlayers(false); }
  }, [selectedCat, tournament.tournament_id]);
  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  const resetForm = () => {
    setPlayerName(""); setPlayerEmail(""); setPlayerPhone(""); setPlayerClub(""); setPlayerGender("M"); setPlayerAgeCategory(""); setPreferences({}); setRestTime(30); setPlayWith([]); setPlayerRanking("4.NC");
    setEditingPlayer(null); setIsCustomRanking(false);
  };

  const openAddDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (player) => {
    setEditingPlayer(player);
    setPlayerName(player.name || "");
    setPlayerEmail(player.email || "");
    setPlayerPhone(player.phone || "");
    setPreferences(player.preferences || {});
    setRestTime(player.rest_time_minutes || 30);
    setPlayWith(player.play_with_players || []);
    setPlayerClub(player.club || "");
    setPlayerGender(player.gender || "M");
    setPlayerAgeCategory(player.age_category || "");
    const r = player.ranking || "4.NC";
    setPlayerRanking(r);
    setIsCustomRanking(!!r && !(r in RANK_VAL));
    setShowDialog(true);
  };

  const savePlayer = async () => {
    if (!playerName.trim()) { toast.error("Inserisci il nome"); return; }
    const payload = {
      name: playerName, email: playerEmail, phone: playerPhone,
      club: playerClub, gender: playerGender, age_category: playerAgeCategory,
      preferences, rest_time_minutes: restTime, play_with_players: playWith,
      ranking: playerRanking,
    };
    try {
      if (editingPlayer) {
        await api.put(`/tournaments/${tournament.tournament_id}/players/${editingPlayer.player_id}`, payload);
        toast.success("Giocatore aggiornato");
      } else {
        await api.post(`/tournaments/${tournament.tournament_id}/categories/${selectedCat}/players`, payload);
        toast.success("Giocatore aggiunto");
      }
      fetchPlayers();
      setShowDialog(false);
      resetForm();
    } catch (e) { toast.error("Errore"); }
  };

  const deletePlayer = async (pid) => { try { await api.del(`/tournaments/${tournament.tournament_id}/players/${pid}`); toast.success("Eliminato"); fetchPlayers(); } catch (e) { toast.error("Errore"); } };

  return (
    <div className="space-y-4">
    <Card className="bg-white">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <CardTitle className="font-heading text-lg font-bold uppercase shrink-0">Giocatori</CardTitle>
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            <Select value={selectedCat} onValueChange={setSelectedCat}>
              <SelectTrigger data-testid="category-select-players" className="w-64 rounded-sm"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
              <SelectContent>{tournament.categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {selectedCat && <Badge variant="outline" className="rounded-sm shrink-0">{players.length} giocatori</Badge>}
            <Button data-testid="add-player-btn" disabled={!selectedCat} onClick={openAddDialog} className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm gap-1 ml-auto" size="sm"><Plus className="h-4 w-4" /> Aggiungi</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!selectedCat ? <p className="text-slate-500 text-center py-8">Seleziona una categoria</p> : loadingPlayers ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-green-700" /></div>
        ) : players.length === 0 ? <p className="text-slate-500 text-center py-8">Nessun giocatore</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-xs uppercase cursor-pointer select-none hover:text-green-700" onClick={() => toggleSort("name")} data-testid="sort-name">
                Nome {sortField === "name" ? (sortDir === "asc" ? "▲" : "▼") : "△"}
              </TableHead>
              <TableHead className="text-xs uppercase cursor-pointer select-none hover:text-green-700" onClick={() => toggleSort("ranking")} data-testid="sort-ranking">
                Ranking {sortField === "ranking" ? (sortDir === "asc" ? "▲" : "▼") : "△"}
              </TableHead>
              <TableHead className="text-xs uppercase">Email</TableHead>
              <TableHead className="text-xs uppercase">Telefono</TableHead>
              <TableHead className="text-xs uppercase">Riposo</TableHead>
              <TableHead className="text-xs uppercase w-20"></TableHead>
            </TableRow></TableHeader>
            <TableBody>{sortedPlayers.map((p) => {
              return (
                <TableRow key={p.player_id} data-testid={`player-row-${p.player_id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell><Badge className="rounded-sm text-xs bg-slate-100 text-slate-700 hover:bg-slate-100 font-mono">{p.ranking || "4.NC"}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-500">{p.email || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{p.phone || "-"}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm text-xs">{p.rest_time_minutes || 30} min</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button data-testid={`edit-player-${p.player_id}`} variant="ghost" size="sm" onClick={() => openEditDialog(p)} className="text-slate-500 hover:text-green-700 h-8 w-8 p-0"><Edit3 className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deletePlayer(p.player_id)} className="text-red-500 hover:text-red-700 h-8 w-8 p-0"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table>
        )}

        {/* Add / Edit Player Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowDialog(open); }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase">{editingPlayer ? "Modifica Giocatore" : "Nuovo Giocatore"}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
                <div><Label className="text-xs font-bold uppercase">Nome *</Label><Input data-testid="player-name-input" value={playerName} onChange={(e) => setPlayerName(e.target.value)} className="mt-1 rounded-sm" /></div>
                <div>
                  <Label className="text-xs font-bold uppercase">Sesso</Label>
                  <Select value={playerGender} onValueChange={setPlayerGender}>
                    <SelectTrigger data-testid="player-gender-select" className="mt-1 rounded-sm w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">M</SelectItem>
                      <SelectItem value="F">F</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-bold uppercase">Email</Label><Input data-testid="player-email-input" value={playerEmail} onChange={(e) => setPlayerEmail(e.target.value)} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs font-bold uppercase">Telefono</Label><Input data-testid="player-phone-input" value={playerPhone} onChange={(e) => setPlayerPhone(e.target.value)} className="mt-1 rounded-sm" /></div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Circolo Tennis / Associazione di appartenenza</Label>
                <Input data-testid="player-club-input" value={playerClub} onChange={(e) => setPlayerClub(e.target.value)} placeholder="Es. TC Roma, ASD Tennis Club..." className="mt-1 rounded-sm" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-bold uppercase">Ranking</Label>
                  {isCustomRanking ? (
                    <div className="flex gap-1 mt-1">
                      <Input
                        data-testid="player-ranking-custom-input"
                        value={playerRanking}
                        onChange={(e) => setPlayerRanking(e.target.value)}
                        placeholder="Es. ITF 200, ELO 1500..."
                        className="rounded-sm flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => { setIsCustomRanking(false); setPlayerRanking("4.NC"); }}
                        className="px-2 text-slate-400 hover:text-slate-700 text-xs border rounded-sm"
                        title="Torna alla lista"
                      >↩</button>
                    </div>
                  ) : (
                    <Select value={playerRanking} onValueChange={(v) => {
                      if (v === "__custom__") { setIsCustomRanking(true); setPlayerRanking(""); }
                      else setPlayerRanking(v);
                    }}>
                      <SelectTrigger data-testid="player-ranking-select" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__custom__" className="text-slate-500 italic">Altro...</SelectItem>
                        {["4.NC","4.6","4.5","4.4","4.3","4.2","4.1","3.6","3.5","3.4","3.3","3.2","3.1","2.8","2.7","2.6","2.5","2.4","2.3","2.2","2.1","1.6","1.5","1.4","1.3","1.2","1.1"].map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">Cat. Età</Label>
                  <Select value={playerAgeCategory} onValueChange={setPlayerAgeCategory}>
                    <SelectTrigger data-testid="player-age-category-select" className="mt-1 rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {["U10","U12","U14","U16","U18","NOR","NOF","O35","O40","O45","O50","O55","O60","O65","O70","O75","O80"].map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase">Riposo (min)</Label>
                  <Input data-testid="rest-time-input" type="number" min="0" value={restTime} onChange={(e) => setRestTime(parseInt(e.target.value) || 30)} className="mt-1 rounded-sm" />
                </div>
              </div>
              <div><Label className="text-xs font-bold uppercase">Preferenze Orario</Label><TimePreferenceGrid preferences={preferences} onChange={setPreferences} /></div>
              <div>
                <Label className="text-xs font-bold uppercase">Preferenza Orario con Altri Giocatori</Label>
                <Select onValueChange={(v) => { if (v && !playWith.includes(v)) setPlayWith([...playWith, v]); }}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona giocatore..." /></SelectTrigger>
                  <SelectContent>{players.filter(p => !playWith.includes(p.player_id) && p.player_id !== editingPlayer?.player_id).map(p => <SelectItem key={p.player_id} value={p.player_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                {playWith.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">{playWith.map(pid => {
                    const p = players.find(pl => pl.player_id === pid);
                    return <Badge key={pid} variant="secondary" className="rounded-sm text-xs gap-1">{p?.name || pid}
                      <button onClick={() => setPlayWith(playWith.filter(x => x !== pid))} className="ml-1 text-slate-400 hover:text-red-500">&times;</button>
                    </Badge>;
                  })}</div>
                )}
                <p className="text-[10px] text-slate-400 mt-1">Preferenza per giocare nello stesso orario o turni adiacenti</p>
              </div>
              <Button data-testid="submit-player-btn" onClick={savePlayer} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">{editingPlayer ? "Salva Modifiche" : "Aggiungi"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
    <PlayerImportCard tournament={tournament} onRefresh={onRefresh} />
    </div>
  );
};

// ==================== VENUES PANEL ====================
const VenuesPanel = ({ tournament, onRefresh }) => {
  const [venues, setVenues] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [venueName, setVenueName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [mapsLink, setMapsLink] = useState("");
  const [numCourts, setNumCourts] = useState(1);
  const [logoFile, setLogoFile] = useState(null);

  const fetchVenues = useCallback(async () => {
    try { setVenues(await api.get(`/tournaments/${tournament.tournament_id}/venues`)); } catch (e) { console.error("Failed to load tournament venues:", e); }
  }, [tournament.tournament_id]);
  useEffect(() => { fetchVenues(); }, [fetchVenues]);

  const addVenue = async () => {
    if (!venueName.trim()) { toast.error("Inserisci il nome"); return; }
    try {
      const v = await api.post(`/tournaments/${tournament.tournament_id}/venues`, { name: venueName, address, phone, google_maps_link: mapsLink, num_courts: numCourts });
      if (logoFile) {
        const formData = new FormData();
        formData.append("file", logoFile);
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tournaments/${tournament.tournament_id}/venues/${v.venue_id}/logo`, { method: "POST", body: formData, credentials: "include" });
      }
      toast.success("Circolo aggiunto"); fetchVenues(); onRefresh?.(); setShowDialog(false);
      setVenueName(""); setAddress(""); setPhone(""); setMapsLink(""); setNumCourts(1); setLogoFile(null);
    } catch (e) { toast.error("Errore"); }
  };

  const deleteVenue = async (vid) => {
    try { await api.del(`/tournaments/${tournament.tournament_id}/venues/${vid}`); toast.success("Eliminato"); fetchVenues(); onRefresh?.(); } catch (e) { toast.error("Errore"); }
  };

  return (
    <Card className="bg-white">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg font-bold uppercase">Info Circolo</CardTitle>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild><Button data-testid="add-venue-btn" className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm gap-1" size="sm"><Plus className="h-4 w-4" /> Aggiungi</Button></DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-xl font-bold uppercase">Nuovo Circolo</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div><Label className="text-xs font-bold uppercase">Nome Circolo *</Label><Input data-testid="venue-name-input" value={venueName} onChange={(e) => setVenueName(e.target.value)} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs font-bold uppercase">Indirizzo</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1 rounded-sm" placeholder="Via Roma 1, Milano" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-bold uppercase">Telefono</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs font-bold uppercase">N. Campi</Label><Input type="number" min="1" value={numCourts} onChange={(e) => setNumCourts(parseInt(e.target.value) || 1)} className="mt-1 rounded-sm" /></div>
              </div>
              <div><Label className="text-xs font-bold uppercase">Link Google Maps</Label><Input value={mapsLink} onChange={(e) => setMapsLink(e.target.value)} className="mt-1 rounded-sm" placeholder="https://maps.google.com/..." /></div>
              <div>
                <Label className="text-xs font-bold uppercase">Logo del Circolo</Label>
                <Input data-testid="venue-logo-input" type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} className="mt-1 rounded-sm" />
              </div>
              <Button data-testid="submit-venue-btn" onClick={addVenue} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Aggiungi Circolo</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {venues.length === 0 ? <p className="text-slate-500 text-center py-8">Nessun circolo aggiunto</p> : (
          <div className="space-y-3">{venues.map(v => (
            <div key={v.venue_id} data-testid={`venue-row-${v.venue_id}`} className="border border-slate-200 rounded-sm p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  {v.logo_path && (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${v.logo_path}`} alt={v.name} className="w-16 h-16 object-contain rounded-sm bg-slate-50 border" />
                  )}
                  <div>
                    <p className="font-medium text-lg">{v.name}</p>
                    {v.address && <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {v.address}</p>}
                    {v.phone && <p className="text-sm text-slate-500">{v.phone}</p>}
                    <Badge variant="outline" className="rounded-sm text-xs mt-1">{v.num_courts} campi</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  {v.google_maps_link && <a href={v.google_maps_link} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="rounded-sm gap-1"><MapPin className="h-3 w-3" /> Mappa</Button></a>}
                  <Button variant="ghost" size="sm" onClick={() => deleteVenue(v.venue_id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}</div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== MAIN ====================
export default function TournamentDetail() {
  const { id } = useParams();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const fetchTournament = useCallback(async () => {
    try { setTournament(await api.get(`/tournaments/${id}`)); } catch (e) { toast.error("Errore"); } finally { setLoading(false); }
  }, [id]);
  const fetchVenues = useCallback(async () => {
    try { setVenues(await api.get(`/tournaments/${id}/venues`)); } catch (e) { console.error("Failed to load venues:", e); }
  }, [id]);
  useEffect(() => { fetchTournament(); fetchVenues(); }, [fetchTournament, fetchVenues]);

  if (loading) return <div className="flex items-center justify-center py-20" data-testid="tournament-loading"><div className="animate-spin h-8 w-8 border-4 border-green-700 border-t-transparent rounded-full" /></div>;
  if (!tournament) return <div className="text-center py-20"><p className="text-slate-500">Torneo non trovato</p><Link to="/dashboard"><Button variant="outline" className="mt-4 rounded-sm">Dashboard</Button></Link></div>;

  return (
    <div className="space-y-6" data-testid="tournament-detail-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Link to="/dashboard"><Button variant="outline" size="sm" className="rounded-sm gap-1" data-testid="back-to-dashboard"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Torneo</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 truncate">{tournament.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="rounded-sm">{tournament.start_date} &rarr; {tournament.end_date}</Badge>
          <Badge variant="secondary" className="rounded-sm">{tournament.categories?.length || 0} cat.</Badge>
        </div>
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-sm w-full sm:w-auto flex-wrap">
          <TabsTrigger data-testid="tab-info" value="info" className="rounded-sm gap-1 data-[state=active]:bg-green-700 data-[state=active]:text-white"><Settings className="h-4 w-4" /> Generale</TabsTrigger>
          <TabsTrigger data-testid="tab-categories" value="categories" className="rounded-sm gap-1 data-[state=active]:bg-green-700 data-[state=active]:text-white"><Trophy className="h-4 w-4" /> Categorie</TabsTrigger>
          <TabsTrigger data-testid="tab-players" value="players" className="rounded-sm gap-1 data-[state=active]:bg-green-700 data-[state=active]:text-white"><Users className="h-4 w-4" /> Giocatori</TabsTrigger>
          <TabsTrigger data-testid="tab-bracket" value="bracket" className="rounded-sm gap-1 data-[state=active]:bg-green-700 data-[state=active]:text-white"><Trophy className="h-4 w-4" /> Tabellone</TabsTrigger>
          <TabsTrigger data-testid="tab-schedule" value="schedule" className="rounded-sm gap-1 data-[state=active]:bg-green-700 data-[state=active]:text-white"><CalendarDays className="h-4 w-4" /> Calendario</TabsTrigger>
        </TabsList>
        <TabsContent value="info" className="mt-6"><InfoPanel tournament={tournament} onUpdate={fetchTournament} /></TabsContent>
        <TabsContent value="categories" className="mt-6"><CategoriesPanel tournament={tournament} onRefresh={fetchTournament} /></TabsContent>
        <TabsContent value="players" className="mt-6"><PlayersPanel tournament={tournament} onRefresh={fetchTournament} /></TabsContent>
        <TabsContent value="bracket" className="mt-6"><BracketPanel tournament={tournament} venue={venues[0]} /></TabsContent>
        <TabsContent value="schedule" className="mt-6"><SchedulePanel tournament={tournament} venue={venues[0]} /></TabsContent>
      </Tabs>
    </div>
  );
}
